import { Dialog, Transition } from "@headlessui/react"
import { XIcon } from "@heroicons/react/outline"
import Fuse from "fuse.js"
import { type Dispatch, Fragment, type SetStateAction, useState } from "react"
import toast from "react-hot-toast"
import type { KeyedMutator } from "swr"
import { useKeyPress } from "../../../../shared/useKeyPress"

interface Props {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  refreshData?: KeyedMutator<{ data: [] }>
  questions: []
  addQuestion: (id) => void
  addStudyQuestion: (id) => void
}

function createQuestion(data) {
  // TODO useCreate
  // TODO create question with full_text, returns questionId
  // addQuestion(questionId)
}

export function QuestionsSlideover(props: Props) {
  const { setOpen, open, refreshData, questions } = props
  const [filteredQuestions, setFilteredQuestions] = useState([])
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [studyQuestionExclusion, setStudyQuestionExclusion] = useState(false)
  const [addingQuestion, setAddingQuestion] = useState(false)
  const [fullText, setFullText] = useState("")
  const [searchText, setSearchText] = useState("")
  const handleChangeFilterText = (event) => {
    const fuse = new Fuse(questions, {
      keys: ["full_text"],
    })
    const result = fuse.search(event.target.value)
    setSearchText(event.target.value)
    setFilteredQuestions(result)
  }

  const handleQuestionClick = (item) => {
    // props.addQuestion(item?.id)
    // toast.success('Saved')
    setSelectedQuestion(item)
    // setFullText(item.full_text)
    // handleChangeFilterText({target: {value: ''}})
  }

  const handleClickNewQuestion = () => {
    setAddingQuestion(true)
    setFullText(searchText)
    setFilteredQuestions([])
  }

  const handleSaveQuestion = async () => {
    const response = await props.addQuestion(fullText)
    const question = response.data.data
    toast.success("Question Saved")
    setSelectedQuestion(question)
    setAddingQuestion(false)
    refreshData()
  }

  const handleClickAddToStudy = () => {
    props.addStudyQuestion({
      question_id: selectedQuestion?.id,
      exclusion_criteria: studyQuestionExclusion,
    })
    setAddingQuestion(false)
    setSelectedQuestion(null)
  }

  useKeyPress(["Escape"], () => props.setOpen(false))

  return (
    <Transition.Root show={props.open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-hidden"
        onClose={props.setOpen}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0" />

          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-400 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-400 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="pointer-events-auto w-screen max-w-2xl">
                <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                  <div className="flex-1">
                    <div
                      className="
                      bg-topo-navy px-4 py-6 sm:px-6"
                    >
                      <div className="flex items-start justify-between space-x-3">
                        <div className="space-y-1">
                          <Dialog.Title className="text-lg font-medium text-gray-100">
                            Questions
                          </Dialog.Title>
                          <p className="text-sm text-gray-300">
                            Click a question below to add it to the study.
                          </p>
                        </div>
                        <div className="flex h-7 items-center">
                          <button
                            type="button"
                            className="text-gray-400 hover:text-gray-500"
                            onClick={() => props.setOpen(false)}
                          >
                            <span className="sr-only">Close panel</span>
                            <XIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {selectedQuestion && (
                      <div className="p-6 space-y-4">
                        <div className="border rounded border-gray-100 p-4 hover:bg-gray-200 cursor-pointer">
                          {selectedQuestion.full_text}
                        </div>
                        <button
                          type="button"
                          className={`mr-2 inline-flex items-center px-4 py-2 border-2 border-gray-100 shadow-sm text-sm leading-4 font-medium rounded-md hover:border-green-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-transparent  ${
                            studyQuestionExclusion === false
                              ? "text-green-700 bg-green-400 outline-none ring-2 ring-offset-2 ring-green-500 dark:bg-green-400"
                              : ""
                          }}`}
                          onClick={() => setStudyQuestionExclusion(false)}
                        >
                          <div className="mr-1">Inclusion</div>
                        </button>
                        <button
                          type="button"
                          className={`inline-flex items-center px-4 py-2 border-2 border-gray-100 shadow-sm text-sm leading-4 font-medium rounded-md hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-transparent ${
                            studyQuestionExclusion === true
                              ? "text-red-700 bg-red-400 outline-none ring-2 ring-offset-2 ring-red-500 dark:bg-red-400"
                              : ""
                          }}`}
                          onClick={() => setStudyQuestionExclusion(true)}
                        >
                          <div className="mr-1">Exclusion</div>
                        </button>

                        <div className="pt-8">
                          <button
                            type="button"
                            onClick={handleClickAddToStudy}
                            className="inline-flex justify-center rounded-md border border-transparent bg-topo-green py-2 px-4 mr-2 text-sm font-medium text-white shadow-sm hover:bg-topo-green-600 focus:bg-topo-green-700 focus:outline-none focus:ring-2 focus:ring-topo-green-800 focus:ring-offset-2"
                          >
                            Add to Study
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-red-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            onClick={() => setSelectedQuestion(null)}
                          >
                            Back
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Divider container */}
                    {!false && (
                      <div className="space-y-6 py-6 sm:space-y-0 sm:divide-y sm:divide-gray-200 sm:py-0">
                        <div className="space-y-4 px-4 sm:px-6 sm:py-5">
                          {!selectedQuestion && (
                            <div>
                              Search for an existing question:
                              <input
                                className="mb-4 block w-1/2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                type="text"
                                onChange={handleChangeFilterText}
                              />
                              <button
                                type="button"
                                onClick={() => handleClickNewQuestion()}
                                className="inline-flex justify-center rounded-md border border-transparent bg-topo-green py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-topo-green-600 focus:bg-topo-green-700 focus:outline-none focus:ring-2 focus:ring-topo-green-800 focus:ring-offset-2"
                              >
                                New Question
                              </button>
                            </div>
                          )}
                          {/* Divider container */}
                          {addingQuestion && (
                            <div className="sm:divide-y sm:divide-gray-200 sm:py-0">
                              <div className="space-y-4 sm:py-5">
                                Add a new question:
                                <textarea
                                  value={fullText}
                                  onChange={(e) => setFullText(e.target.value)}
                                  className="mb-4 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                  type="text"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={handleSaveQuestion}
                                className="inline-flex justify-center rounded-md border border-transparent bg-topo-green py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-topo-green-600 focus:bg-topo-green-700 focus:outline-none focus:ring-2 focus:ring-topo-green-800 focus:ring-offset-2"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-red-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                onClick={() => setAddingQuestion(false)}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                          {filteredQuestions?.map((result) => (
                            <div
                              key={result?.item?.id}
                              className="border rounded border-gray-100 p-4 hover:bg-gray-200 cursor-pointer"
                              onClick={() => handleQuestionClick(result.item)}
                              onKeyUp={() => handleQuestionClick(result.item)}
                            >
                              {result?.item?.full_text}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Action buttons */}
                  <div className="flex-shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                    <div className="flex justify-start space-x-3">
                      {/* <button
                        type="button"
                        onClick={handleClickAdd}
                        className="inline-flex justify-center rounded-md border border-transparent bg-topo-green py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-topo-green-600 focus:bg-topo-green-700 focus:outline-none focus:ring-2 focus:ring-topo-green-800 focus:ring-offset-2"
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-red-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        onClick={() => props.setOpen(false)}
                      >
                        Cancel
                      </button> */}
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
